import { useCallback, useEffect, useState } from "react";

import { useArtwork, useArtworkForm } from "@design-lab/entities/artwork";
import { useCompositionForm } from "@design-lab/entities/composition";
import { createDefaultArtworkData } from "@design-lab/entities/composition/lib/design-project";
import { useSelectProductComposition } from "@design-lab/entities/composition/model/useSelectProductComposition";
import { useFetchPrintOnDemandProduct } from "@design-lab/entities/product";
import { mapPdpQuantitiesToDesignLab } from "@design-lab/shared/lib/pdp-persistence";
import { useAutoUploadDetection } from "./useAutoUploadDetection";

const DEFAULT_CATALOG_ID = "G500";

export function useStartDesigning() {
  const { form: artworkForm } = useArtworkForm();
  const { form: compositionForm } = useCompositionForm();
  const { selectProductComposition } = useSelectProductComposition();
  const { front, back } = useArtwork();
  const fetchProduct = useFetchPrintOnDemandProduct();
  const {
    catalogNumber,
    activeComposition,
    clearAutoUpload,
    openStartDesigning,
    pdpQuantities,
    pdpSizeData
  } = useAutoUploadDetection();

  const [isStartDesigningOpen, setIsStartDesigningOpen] = useState(false);

  const closeStartDesigning = useCallback(() => setIsStartDesigningOpen(false), []);
  const openStartDesigningModal = useCallback(() => setIsStartDesigningOpen(true), []);

  useEffect(() => {
    if (openStartDesigning) {
      setIsStartDesigningOpen(true);
    }
  }, [openStartDesigning]);

  const resetStartDesigning = useCallback(async () => {
    closeStartDesigning();
    artworkForm.reset(createDefaultArtworkData());
    front.send({ type: "reset" });
    back.send({ type: "reset" });

    compositionForm.setValue("addedCompositions" as any, []);
    compositionForm.setValue("currentCompositionId" as any, "");

    const targetCatalog = catalogNumber || DEFAULT_CATALOG_ID;

    // First fetch the product to get variant info
    const product = await fetchProduct(targetCatalog);
    if (!product || !product.compositions) return;

    // Find the exact composition by activeComposition, or fall back to default
    let targetComposition = product.compositions.find(
      (c: any) => c.colorName === activeComposition
    );

    // If activeComposition not found, use default composition
    if (!targetComposition) {
      targetComposition = product.compositions.find((c: any) => c.default);
    }

    if (!targetComposition) return;

    // Build variants from PDP data using the ACTUAL composition we're going to use
    let variants: Record<string, number> = {};
    if (pdpQuantities && pdpSizeData) {
      variants = mapPdpQuantitiesToDesignLab(
        pdpQuantities,
        pdpSizeData,
        targetComposition.variants
      );
    }

    // Use the ACTUAL composition's colorName (not activeComposition if it doesn't exist)
    await selectProductComposition({
      catalogNumber: targetCatalog,
      colorName: targetComposition.colorName,
      mode: "replace",
      variants
    });

    // Clear auto-upload/open flags only after state is reset and initialized
    clearAutoUpload();
  }, [
    closeStartDesigning,
    artworkForm,
    front,
    back,
    compositionForm,
    catalogNumber,
    clearAutoUpload,
    fetchProduct,
    activeComposition,
    selectProductComposition,
    pdpQuantities,
    pdpSizeData
  ]);

  const onContinueDesigning = useCallback(async () => {
    closeStartDesigning();
    const targetCatalog = catalogNumber || DEFAULT_CATALOG_ID;

    // First fetch the product to get variant info
    const product = await fetchProduct(targetCatalog);
    if (!product || !product.compositions) return;

    // Find the exact composition by activeComposition, or fall back to default
    let targetComposition = product.compositions.find(
      (c: any) => c.colorName === activeComposition
    );

    // If activeComposition not found, use default composition
    if (!targetComposition) {
      targetComposition = product.compositions.find((c: any) => c.default);
    }

    if (!targetComposition) return;

    // Build variants from PDP data using the ACTUAL composition we're going to use
    let variants: Record<string, number> = {};
    if (pdpQuantities && pdpSizeData) {
      variants = mapPdpQuantitiesToDesignLab(
        pdpQuantities,
        pdpSizeData,
        targetComposition.variants
      );
    }

    // Use the ACTUAL composition's colorName (not activeComposition if it doesn't exist)
    await selectProductComposition({
      catalogNumber: targetCatalog,
      colorName: targetComposition.colorName,
      variants
    });

    // Clear auto-upload/open flags only after state is reset and initialized
    clearAutoUpload();
  }, [
    catalogNumber,
    activeComposition,
    fetchProduct,
    closeStartDesigning,
    clearAutoUpload,
    selectProductComposition,
    pdpQuantities,
    pdpSizeData
  ]);

  return {
    resetStartDesigning,
    isStartDesigningOpen,
    closeStartDesigning,
    openStartDesigningModal,
    onContinueDesigning
  };
}
